/**
 * parse-brand-custom.js
 * 解析自定义平台样式配置
 * 解析结果
    {
        "page":{
            "type":"route",
            "gl-userlist":{
                "tenant":"bj_xicheng",
                "value":"user"
            }
        },
        "activity":{
            "type":"app_template",
            "create_template":{
                "scene":"例如group表示协助组下活动生效，不可为空",
                "value":"v1"
            }
        }
    }
 */

// 当前站点自定义的平台样式数据
let currentCustomData = {};
let currentBrand = ''; // 当前品牌
let currentTenant = ''; // 当前空间
/**
 * 字段解析优先级：brands > tenants > scenes （从左往右取，只要取到一层就不会再往下取了）
 * scenes 在任何内容都取不到的情况下，才会取scenes
 */
const FIELD_PRIORITY = [
    {
        key: 'brands',
        field: 'brand'
    },
    {
        key: 'tenants',
        field: 'tenant'
    },
    {
        key: 'scenes',
        field: 'scene'
    }
];

/**
 * 解析自定义平台样式配置，解析出当前站点下可用的所有可用的配置项
 * 1. 支持平台页面显示元素、组件、页面风格配置
 * 2. 活动模板配置：不同站点可从这里取对应的活动模板配置，解决了平台写死活动模板的问题
 * @param {Object} data /tenant-center/config/brand/custom 返回的自定义平台样式配置对像，解析其下面的 configTypes
 * @param {String} brand 品神
 * @param {String} tenant 空间
 */
function parseBrandCustom(data = {}, brand, tenant) {
    currentBrand = brand;
    currentTenant = tenant;
    let res = (data.configTypes || []).reduce((acc, item) => {
        let obj = getDataByFieldPriority(item.codes, { brand, tenant});
        acc[item.owner] = {
            type: item.type,
            ...obj
        };
        return acc;
    }, {});
    currentCustomData = res;
    return res;
}

/**
 * 根据字段优先缓获取内容：字段解析优先级：brands > tenants > scenes （从左往右取，只要取到一层就不会再往下取了）
 * @param {*} data 获取的数据源
 * @param {Object} params 对象属性有：{brand: '', tenant: ''} 对应 FIELD_PRIORITY 中的 key 属性值
 */
function getDataByFieldPriority(data, params) {
    let fieldData = {};
    for(let key in data) { // key值为模块值，例如：gl-userlis, create_template
        // 根据优先级 FIELD_PRIORITY 顺序查找配置，只要匹配到一个就不会往下执行了
        for(let priority of FIELD_PRIORITY) {
            let arr = data[key]?.[priority.key] || [];
            if(priority.key === 'scenes') {
                // brands和tenants内容没有匹配到就取默认取scenes
                fieldData[key] = arr;
            } else {
                let temp = arr.reduce((res, item) => {
                    if(item[priority.field] === params[priority.field]) {
                        res.push(item);
                    }
                    return res;
                }, []);
                if(temp.length > 0) {
                    fieldData[key] = temp;
                    break;
                }
            }
        }
    }
    return fieldData;
}


/**
 * 获取平台：页面、应用定制信息
 * @param {String} owner 所属的模块信息（页面/应用），如：page=页面，activity=活动（应用）
 * @param {String} name 具体模功能模块，配合owner使用，如：page->gl-userlist(用户列表)=页面，activity->create_template=创建模板
 * @param {String} scene 场景信息，非必须，如果需要则传场景信息。如：协作组下创建活动跟其他位置使用的活动模板不一致，则需要传入group信息
 * 
 * 使用举例：
 * 管理中心用户列表权限（页面级）：getSysCustomValue('page', 'gl-userlist')
 * 活动-创建活动权限（应用级）：getSysCustomValue('activity', 'create_template')
 * 活动-创建活动权限（应用级 + 场景）：getSysCustomValue('activity', 'create_template', 'group') group表示协作组场景
 * 活动-创建活动权限（应用级 + 场景）：getSysCustomValue('activity', 'create_template', 'xinjiangershi_teacher')
 */
function getSysCustomValue(owner, name, scene = '') {
    let arr = currentCustomData[owner]?.[name] || [];
    let obj = arr.find(item => {
        if(scene) {
            // 有场景信息，匹配场景情况
            return item.scene === scene;
        } else {
            // 无场景信息，需要注意返回的数据里是否有场景信息如果有不能使用
            return !item.scene; 
        }
    });
    return obj?.value || '';
}

export { 
    parseBrandCustom,
    getSysCustomValue
};

/*
 测试数据
 const data = {
    'configTypes': [
        {
            'type': 'route',
            'owner': 'page',
            'codes': {
                'gl-userlist': {
                    'tenants': [
                        {
                            'tenant': 'bj_xicheng',
                            'value': 'user'
                        },
                        {
                            'tenant': 'xcjyyxw',
                            'value': 'user2'
                        }
                    ]
                }
            }
        },
        {
            'type': 'app_template',
            'owner': 'activity',
            'codes': {
                'create_template': {
                    'tenants': [
                        {
                            'tenant': '空间值，此处不可为空',
                            'scene': '例如group表示协助组下活动生效，可以为空',
                            'value': 'v1'
                        }
                    ],
                    'brands': [
                        {
                            'brand': 'bj_xicheng111',
                            'scene': 'group',
                            'value': 'brand_v1'
                        },
                        {
                            'brand': 'bj_xicheng111',
                            'scene': 'userCenter',
                            'value': 'brand_v2'
                        }
                    ],
                    'scenes': [
                        {
                            'scene': '例如group表示协助组下活动生效，不可为空',
                            'value': 'v1'
                        },
                        {
                            'scene': 'personalCenter',
                            'value': 'personalCenter_v2'
                        },
                        {
                            'scene': 'schoolCenter',
                            'value': 'schoolCenter_v3'
                        }
                    ]
                }
            }
        }
    ]
};
*/